<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { useAdvertisersListStore } from '@/stores/advertisers-list.stores'
import { BoxComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const router = useRouter()
const advertisersStore = useAdvertisersListStore()

const shouldLimitHeight = computed(() => advertisersStore.list.length > 5)
const hasMultipleAdvertisers = computed(() => advertisersStore.list.length > 1)

const handleSelect = async (advertiserId: number) => {
  await advertisersStore.selectAdvertiser(advertiserId)
  await router.push({ name: 'overview' })
}
</script>
<template>
  <el-select
    v-if="hasMultipleAdvertisers && advertisersStore.currentAdvertiser"
    :model-value="advertisersStore.currentAdvertiser?.id"
    class="advertiser-selector"
    size="large"
    :popper-class="shouldLimitHeight ? 'full-height' : ''"
    @change="handleSelect"
  >
    <template #label>
      <FlexComponent direction="row" align="center" :gap="10">
        <img
          v-if="advertisersStore.currentAdvertiser?.logoUrl"
          :src="advertisersStore.currentAdvertiser.logoUrl"
          :alt="advertisersStore.currentAdvertiser.name"
          class="advertiser-selector__logo"
        />
        <BoxComponent v-else :h="35" :w="50" />
        <FlexComponent direction="column" justify="center" class="advertiser-selector__label">
          <TypographyComponent variant="label-small" color="#808080">Selected advertiser</TypographyComponent>
          <TypographyComponent variant="body-medium" color="var(--el-color-primary)" text-truncate>
            {{ advertisersStore.currentAdvertiser?.name }}
          </TypographyComponent>
        </FlexComponent>
      </FlexComponent>
    </template>
    <el-option v-for="item in advertisersStore.list" :key="item.id" :label="item.name" :value="item.id">
      <FlexComponent direction="row" align="center" :gap="10">
        <img v-if="item.logoUrl" :src="item.logoUrl" :alt="item.name" class="advertiser-selector__logo" />
        <BoxComponent v-else :h="35" :w="50" />
        <FlexComponent v-if="item.id === advertisersStore.currentId" direction="column" justify="center">
          <TypographyComponent variant="label-small" color="#808080">Selected advertiser</TypographyComponent>
          <TypographyComponent variant="body-medium" :font-weight="700" color="var(--el-color-primary)">
            {{ item.name }}
          </TypographyComponent>
        </FlexComponent>
        <TypographyComponent v-else variant="body-medium">{{ item.name }}</TypographyComponent>
      </FlexComponent>
    </el-option>
  </el-select>
  <FlexComponent
    v-else-if="advertisersStore.currentAdvertiser"
    class="advertiser-selector advertiser-selector--single"
    direction="row"
    align="center"
    :gap="10"
  >
    <img
      v-if="advertisersStore.currentAdvertiser?.logoUrl"
      :src="advertisersStore.currentAdvertiser.logoUrl"
      :alt="advertisersStore.currentAdvertiser.name"
      class="advertiser-selector__logo"
    />
    <BoxComponent v-else :h="35" :w="50" />
    <FlexComponent direction="column" justify="center" class="advertiser-selector__label">
      <TypographyComponent variant="body-medium" color="var(--el-color-primary)" text-truncate>
        {{ advertisersStore.currentAdvertiser?.name }}
      </TypographyComponent>
    </FlexComponent>
  </FlexComponent>
</template>
<style lang="scss" scoped>
.advertiser-selector {
  max-width: 280px;
  overflow: hidden;
  @media screen and (max-width: 767px) {
    max-width: 160px;
  }
}

.advertiser-selector--single {
  flex-shrink: 0;
}

.advertiser-selector__label {
  min-width: 0;
  overflow: hidden;
}

.el-select-dropdown__item {
  height: 52px;
  display: flex;
  align-items: center;
}

.advertiser-selector__logo {
  object-fit: contain;
  max-width: 50px;
  max-height: 35px;
  aspect-ratio: 2 / 1;
  width: 100%;
  flex-shrink: 0;
}
</style>
<style lang="scss">
.full-height .el-select-dropdown__wrap {
  max-height: 290px !important;
  overflow-y: auto !important;
}
</style>
